import { getAuthHeader } from "./authHeader";
import axios from "axios";
import { CONTACTS_API as API_BASE, API_BASE_URL } from "../apiConstants";
import { apiCall, createApiMethod } from "../utils/apiErrorHandler";
import { withTimeout, getTimeoutForOperation, TIMEOUT_CONFIG } from "./api";

import { toCamel } from "../utils/caseConverter";

// An explicit empty company clears the optional association; an empty assignee is omitted.
const omitIfEmpty = ["assigned_to_id"];

// Helper: Convert camelCase to snake_case for API payloads
const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== "object") return obj;
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    const mappedKey = key.replace(/([A-Z])/g, "_$1").toLowerCase();
    // Skip empty strings for ID fields
    if (
      omitIfEmpty.includes(mappedKey) &&
      (value === "" || value === null || value === undefined)
    ) {
      continue;
    }
    result[mappedKey] = mappedKey === "company_id" && value === "" ? null : value;
  }
  return result;
};

// Helper: Wrap payload in 'contact' key with snake_case conversion
const wrapContactPayload = (data) => {
  return { contact: toSnakeCase(data) };
};

const fetchContact = async (id) => {
  const config = withTimeout(getAuthHeader(), getTimeoutForOperation("quick")); // 10s for single resource
  const response = await axios.get(`${API_BASE}/${id}`, config);
  return response.data;
};

const contactApi = {
  // Updated getAll to accept query parameters for pagination, search, and filtering
  getAll: createApiMethod(
    async (params = {}) => {
      const authConfig = getAuthHeader();
      const config = withTimeout(
        {
          params: toSnakeCase(params),
          ...authConfig,
        },
        getTimeoutForOperation("quick"),
      ); // 10s for list queries

      const response = await axios.get(API_BASE, config);
      // Backend returns { contacts, page, pages, total }
      return response.data;
    },
    { context: { service: "contact", method: "getAll" } },
  ),

  // Organization-wide totals per status (the list endpoint reports `total`).
  getStatusCounts: createApiMethod(
    async (statuses) => {
      const authConfig = getAuthHeader();
      // One failed status must not blank the others: a status that failed is
      // null (the page shows it as unavailable); only a total failure throws.
      const results = await Promise.allSettled(
        statuses.map(async (status) => {
          const config = withTimeout(
            { params: { page: 1, limit: 1, status }, ...authConfig },
            getTimeoutForOperation("quick"),
          );
          const response = await axios.get(API_BASE, config);
          return [status, response.data?.total ?? null];
        }),
      );
      const failed = results.filter((result) => result.status === "rejected");
      if (failed.length === results.length && failed.length > 0) {
        throw failed[0].reason;
      }
      return Object.fromEntries(
        results.map((result, index) =>
          result.status === "fulfilled" ? result.value : [statuses[index], null],
        ),
      );
    },
    { context: { service: "contact", method: "getStatusCounts" } },
  ),

  getById: createApiMethod(fetchContact, { context: { service: "contact", method: "getById" } }),

  // The dialing session's "who and why" column: last touch, call history in
  // numbers, open deal and account facts. Quiet: a missing section is omitted
  // on screen, never an error the rep has to act on.
  getSessionContext: createApiMethod(
    async (id) => {
      const config = withTimeout(getAuthHeader(), getTimeoutForOperation("quick"));
      const response = await axios.get(`${API_BASE}/${id}/session-context`, config);
      return toCamel(response.data?.data ?? null);
    },
    { showErrorToast: false, context: { service: "contact", method: "getSessionContext" } },
  ),

  // The same read without the error toast, for background lookups such as the
  // record names on Ask Nexus sources: a name that cannot load is not an error
  // the user has to act on.
  getByIdQuietly: createApiMethod(fetchContact, {
    showErrorToast: false,
    context: { service: "contact", method: "getByIdQuietly" },
  }),

  create: createApiMethod(
    async (contactData) => {
      const authConfig = getAuthHeader();
      const config = withTimeout(authConfig, getTimeoutForOperation("normal")); // 30s for create

      const response = await axios.post(
        API_BASE,
        wrapContactPayload(contactData),
        config,
      );
      return response.data;
    },
    { context: { service: "contact", method: "create" } },
  ),

  update: createApiMethod(
    async (id, contactData) => {
      const authConfig = getAuthHeader();
      const config = withTimeout(authConfig, getTimeoutForOperation("normal")); // 30s for update

      const response = await axios.put(
        `${API_BASE}/${id}`,
        wrapContactPayload(contactData),
        config,
      );
      return response.data;
    },
    { context: { service: "contact", method: "update" } },
  ),

  delete: createApiMethod(
    async (id) => {
      const authConfig = getAuthHeader();
      const config = withTimeout(authConfig, getTimeoutForOperation("quick")); // 10s for delete

      const response = await axios.delete(`${API_BASE}/${id}`, config);
      return response.data; // Or handle potential no-content response
    },
    { context: { service: "contact", method: "delete" } },
  ),

  // Remove contact from a specific campaign (keeps the contact, removes campaign association)
  removeFromCampaign: createApiMethod(
    async (contactId, campaignId) => {
      const authConfig = getAuthHeader();
      const config = withTimeout(authConfig, getTimeoutForOperation("quick")); // 10s for operation

      const response = await axios.post(
        `${API_BASE}/${contactId}/remove-from-campaign`,
        { campaignId },
        config,
      );
      return response.data;
    },
    { context: { service: "contact", method: "removeFromCampaign" } },
  ),

  // Batch operations with longer timeout
  batchUpdate: createApiMethod(
    async (ids, updateData) => {
      const authConfig = getAuthHeader();
      const config = withTimeout(authConfig, getTimeoutForOperation("batch")); // 3 minutes for batch

      const response = await axios.post(
        `${API_BASE}/batch-update`,
        {
          ids,
          data: updateData,
        },
        config,
      );
      return response.data;
    },
    { context: { service: "contact", method: "batchUpdate" } },
  ),

  // Import contacts with file upload timeout
  importContacts: createApiMethod(
    async (file, options = {}) => {
      const authConfig = getAuthHeader();
      const formData = new FormData();
      formData.append("file", file);

      Object.keys(options).forEach((key) => {
        formData.append(key, options[key]);
      });

      const config = withTimeout(
        {
          ...authConfig,
          headers: { ...authConfig.headers },
        },
        getTimeoutForOperation("upload"),
      ); // 2 minutes for file upload

      const response = await axios.post(`${API_BASE}/import`, formData, config);
      return toCamel(response.data);
    },
    { retries: 0, showErrorToast: false, timeout: TIMEOUT_CONFIG.upload,
      context: { service: "contact", method: "importContacts" } },
  ),

  getImportStatus: createApiMethod(
    async (id) => {
      const response = await axios.get(
        `${API_BASE_URL}/contact-imports/${encodeURIComponent(id)}`,
        withTimeout(getAuthHeader(), getTimeoutForOperation("quick")),
      );
      return toCamel(response.data);
    },
    { retries: 0, showErrorToast: false,
      context: { service: "contact", method: "getImportStatus" } },
  ),

  // Export contacts with download timeout
  exportContacts: createApiMethod(
    async (params = {}) => {
      const authConfig = getAuthHeader();
      const config = withTimeout(
        {
          ...authConfig,
          params,
          responseType: "blob",
        },
        getTimeoutForOperation("download"),
      ); // 1 minute for download

      const response = await axios.get(`${API_BASE}/export`, config);
      return response.data;
    },
    { context: { service: "contact", method: "exportContacts" } },
  ),
};

export default contactApi;
